import {
    SkyNetTableConfig, SkyNetTableField,
} from 'app/shared-components/SkyNetTable'
import {
    CommonRoutes, FilterType,
} from 'app/enums/common.enums'
import {
    ContainersTablesTab,
} from 'app/enums/domain.enums'
import CloudIconBoolean from 'app/Apps/DomainObject/ComponentMapper/CloudIconBoolean'
import getReadingThresholdType, {
    ReadingThresholdType,
} from 'app/utils/getReadingThresholdType'
import {
    convertFromFilterDaysValue,
    convertToFilterDaysValue,
    parseFromFilterDaysValue,
    parseToFilterDaysValue,
} from 'app/utils/date'
import {
    LoggerType,
} from '../../Loggers'

import {
    Containers,
} from './containers.types'

const daysFilterOptions = {
    labels: {
        from: 'More or Equal (Days)',
        to: 'Less Then (Days)',
    },
    convert: {
        from: convertFromFilterDaysValue,
        to: convertToFilterDaysValue,
    },
    parse: {
        from: parseFromFilterDaysValue,
        to: parseToFilterDaysValue,
    },
}

const longTermLease: SkyNetTableField<keyof Containers> = {
    name: 'currentLongTermLeaseNumber',
    sorting: false,
    width: '100px',
    labelKey: 'Container.currentLongTermLeaseNumber',
}

const defaultFields: SkyNetTableField<keyof Containers>[] = [
    {
        name: 'serialNumber',
        reduced: true,
        width: '130px',
    },
    {
        name: 'containerTypeContainerModel',
        filterType: FilterType.Array,
        filterField: 'containerTypeContainerModel',
        width: '170px',
    },
    {
        name: 'tempRange',
        filterType: FilterType.Array,
        filterField: 'tempRange',
        componentName: 'ContainerTempRange',
        width: '150px',
    },
    {
        name: 'containerStatusEnriched',
        filterType: FilterType.Array,
        reduced: true,
        filterField: 'containerStatusEnriched',
        componentName: 'ContainerStatusEnriched',
        minWidth: '200px',
    },
    {
        name: 'containerStatusEnrichedChangedOnUtc',
        componentName: 'StatusAge',
        width: '160px',
    },
    {
        name: 'currentOrderOrderNumber',
        filterField: 'currentOrderOrderNumber',
        filterType: FilterType.Array,
        width: '120px',
    },
    {
        name: 'locationLocationName',
        filterField: 'locationLocationName',
        filterType: FilterType.Array,
        reduced: true,
        minWidth: '200px',
    },
    {
        name: 'defaultInternalLoggerType',
        filterField: 'defaultInternalLoggerType',
        minWidth: '200px',
        filterType: FilterType.Array,
        componentName: 'EnumValue',
        allowedValuesEnum: LoggerType,
        labelKey: 'Container.defaultInternalLoggerType',
    },
    {
        name: 'batteryLevelInternal',
        labelKey: 'Container.batteryLevel',
        minWidth: '115px',
        mapCellProps: ({
            batteryLevelInternal,
        }) => {
            return {
                children: batteryLevelInternal && `${Math.round(batteryLevelInternal * 100)}%`,
            }
        },
    },
    {
        name: 'lastMeasuredTempInternal',
        componentName: 'ContainerTemp',
        propsMapper: {
            lowerTempBoundary: 'containerTypeIntTempRangeStart',
            higherTempBoundary: 'containerTypeIntTempRangeEnd',
        },
        width: '100px',
        sorting: false,
        filterField: 'lastMeasuredTempInternal',
        filterType: FilterType.IntegerRange,
        filterOptions: {
            labels: {
                from: 'Min Temperature',
                to: 'Max Temperature',
            },
        },
    },
    {
        name: 'connectedInternal',
        labelKey: 'SkyCube.connected',
        width: '90px',
        sorting: false,
        mapCellProps: ({
            lastMeasuredOnUtcInternal,
        }) => {
            return {
                value: getReadingThresholdType(lastMeasuredOnUtcInternal)
                    === ReadingThresholdType.LIVE,
            }
        },
        Cell: CloudIconBoolean,
    },
    {
        name: 'lastMeasuredOnUtcInternal',
        componentName: 'Age',
        width: '120px',
        sorting: false,
        filterField: 'lastMeasuredOnUtcInternal',
        filterType: FilterType.IntegerRange,
        filterOptions: daysFilterOptions,
    },
    {
        name: 'loggerExchangedOnUtcInternal',
        componentName: 'LoggerAge',
        width: '150px',
        filterField: 'loggerExchangedOnUtcInternal',
        filterType: FilterType.IntegerRange,
        filterOptions: daysFilterOptions,
    },
    {
        name: 'defaultAmbientLoggerType',
        filterField: 'defaultAmbientLoggerType',
        minWidth: '200px',
        filterType: FilterType.Array,
        componentName: 'EnumValue',
        allowedValuesEnum: LoggerType,
        labelKey: 'Container.defaultAmbientLoggerType',
    },
    {
        name: 'batteryLevelAmbient',
        labelKey: 'Container.batteryLevel',
        minWidth: '115px',
        mapCellProps: ({
            batteryLevelAmbient,
        }) => {
            return {
                children: batteryLevelAmbient && `${Math.round(batteryLevelAmbient * 100)}%`,
            }
        },
    },
    {
        name: 'lastMeasuredTempAmbient',
        componentName: 'ContainerTemp',
        width: '100px',
        sorting: false,
        filterField: 'lastMeasuredTempAmbient',
        filterType: FilterType.IntegerRange,
        filterOptions: {
            labels: {
                from: 'Min Temperature',
                to: 'Max Temperature',
            },
        },
    },
    {
        name: 'connectedAmbient',
        labelKey: 'SkyCube.connected',
        width: '90px',
        sorting: false,
        mapCellProps: ({
            lastMeasuredOnUtcAmbient,
        }) => {
            return {
                value: getReadingThresholdType(lastMeasuredOnUtcAmbient)
                    === ReadingThresholdType.LIVE,
            }
        },
        Cell: CloudIconBoolean,
    },
    {
        name: 'lastMeasuredOnUtcAmbient',
        componentName: 'Age',
        width: '120px',
        sorting: false,
        filterField: 'lastMeasuredOnUtcAmbient',
        filterType: FilterType.IntegerRange,
        filterOptions: daysFilterOptions,
    },
    {
        name: 'loggerExchangedOnUtcAmbient',
        componentName: 'LoggerAge',
        width: '120px',
        filterField: 'loggerExchangedOnUtcAmbient',
        filterType: FilterType.IntegerRange,
        filterOptions: daysFilterOptions,
    },
]

const fieldsWithLongTermLease = [
    ...defaultFields.slice(0, 5),
    longTermLease,
    ...defaultFields.slice(5),
]

const tableConfig: SkyNetTableConfig<keyof Containers> = {
    name: 'ContainersTable',
    tabs: [
        {
            name: 'Operational Containers',
            url: ContainersTablesTab.Operational,
            defaultTab: true,
            fields: fieldsWithLongTermLease,
        },
        {
            name: 'All Containers',
            url: CommonRoutes.All,
            fields: fieldsWithLongTermLease,
        },
        {
            name: 'Excluded',
            url: CommonRoutes.Excluded,
            fields: defaultFields,
        },
    ],

}

export default tableConfig
